import React, { useContext, useEffect, useMemo, useState } from "react";

import {
    Add,
    CompareArrows,
    PersonAddAlt,
    Print,
    Receipt,
} from "@mui/icons-material";
import { ListItemIcon } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import axios from "axios";
import SwitchTableAlert from "../../../components/SwitchTableAlert";
import { PosContext } from "../../../shared/PosContext";
import { usePrintOrder } from "../../../shared/hooks/useTablesData";
import MoveOrdersDialog from "../../Widgets/MoveOrdersDialog";
import DragableTable from "./DragableTable";
import PrintOrderModal from "./PrintOrderModal";
import { useStyles } from "./styles";
import { AppContext } from "../../../AppContext";
import { isRoleService } from "../../../frappeConfig";

const Room = ({
    room,
    onTableSelect,
    moving,
    handleOnChangeMove,
    handleCloseTableExt,
}) => {
    const classes = useStyles();

    const {
        tables,
        selectedTable,
        setSelectedTable,
        refetchTablesData,
        discountEnabled,
    } = useContext(PosContext);

    const { restaurantSettings, userInfo } = useContext(AppContext);

    const [selectable, setSelectable] = useState(false);
    const [backgroundUrl, setBackgroundUrl] = useState("");
    const [imageWidth, setImageWidth] = useState(0);
    const [imageHeight, setImageHeight] = useState(0);

    const [oreginalImageWidth, setOreginalImageWidth] = useState(0);
    const [oreginalImageHeight, setOreginalImageHeight] = useState(0);

    const [movingOrdersOpen, setMovingOrdersOpen] = useState(false);
    const printOrder = usePrintOrder();

    const [printOrderModal, setPrintOrderModal] = useState(false);
    const togglePrintOrder = () => setPrintOrderModal(!printOrderModal);

    useEffect(() => {
        setBackgroundUrl(null);
        if (room) {
            let params = {
                doctype: "Restaurant Object",
                name: room.name,
            };
            if (params.name) {
                const { data } = axios
                    .post(
                        "/method/frappe.desk.form.load.getdoc",
                        JSON.stringify(params)
                    )
                    .then((res) => {
                        const floorPlanUrl =
                            res.data.docs?.[0]?.floor_plan || "";

                        if (!floorPlanUrl) {
                            console.warn("No floor plan URL found in docs");
                            return;
                        }

                        const image = new Image();
                        image.crossOrigin = "anonymous"; // Handle potential CORS issues

                        image.onload = () => {
                            const width = image.width;
                            const height = image.height;
                            setOreginalImageWidth(width);
                            setOreginalImageHeight(height);

                            if (width != document.body.clientWidth) {
                                let newWidth = document.body.clientWidth * 0.98;
                                let newHeight = (height * newWidth) / width;
                                const posHeaderElement =
                                    document.getElementById("Pos_Header") || {};

                                const maxHeight =
                                    window.innerHeight -
                                    (posHeaderElement.clientHeight || 0);

                                if (maxHeight < newHeight) {
                                    newHeight = maxHeight;
                                    newWidth = (width * newHeight) / height;
                                }

                                setImageWidth(newWidth);
                                setImageHeight(newHeight);
                            } else {
                                setImageWidth(width);
                                setImageHeight(height);
                            }

                            // Use the same URL that we loaded for dimensions, properly encoded for CSS
                            setBackgroundUrl(encodeURI(floorPlanUrl));
                        };

                        image.onerror = (error) => {
                            // Fallback to attachment URL if direct floor_plan fails
                            const attachmentUrl =
                                res.data.docinfo.attachments?.[0]?.file_url ||
                                "";
                            if (
                                attachmentUrl &&
                                attachmentUrl !== floorPlanUrl
                            ) {
                                console.log("Fallback URL:", attachmentUrl);
                                const fallbackImage = new Image();
                                fallbackImage.crossOrigin = "anonymous";

                                fallbackImage.onload = () => {
                                    const width = fallbackImage.width;
                                    const height = fallbackImage.height;
                                    console.log(
                                        "Fallback image dimensions:",
                                        width,
                                        height
                                    );
                                    setOreginalImageWidth(width);
                                    setOreginalImageHeight(height);

                                    if (width != document.body.clientWidth) {
                                        let newWidth =
                                            document.body.clientWidth * 0.98;
                                        let newHeight =
                                            (height * newWidth) / width;
                                        const posHeaderElement =
                                            document.getElementById(
                                                "Pos_Header"
                                            ) || {};

                                        const maxHeight =
                                            window.innerHeight -
                                            (posHeaderElement.clientHeight ||
                                                0);

                                        if (maxHeight < newHeight) {
                                            newHeight = maxHeight;
                                            newWidth =
                                                (width * newHeight) / height;
                                        }

                                        setImageWidth(newWidth);
                                        setImageHeight(newHeight);
                                    } else {
                                        setImageWidth(width);
                                        setImageHeight(height);
                                    }

                                    setBackgroundUrl(encodeURI(attachmentUrl));
                                };

                                fallbackImage.onerror = () => {
                                    console.error(
                                        "Both floor plan and attachment URLs failed to load"
                                    );
                                };

                                fallbackImage.src = attachmentUrl;
                            }
                        };

                        image.src = floorPlanUrl;
                    })
                    .catch((error) => {
                        console.error("Error fetching room data:", error);
                    });
            }
        }
    }, [room]);

    const tableClick = (table) => {
        if (onTableSelect) onTableSelect(table);
    };

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpenMenu = (event, table) => {
        setAnchorEl(event.currentTarget);
        setSelectedTable(table);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedTable(null);
    };

    const handleCloseTable = () => {
        setAnchorEl(null);
        if (selectedTable) {
            if (handleCloseTableExt) {
                handleCloseTableExt(selectedTable);
            }
        }
    };

    const handleOrder = () => {
        setAnchorEl(null);

        if (selectedTable) {
            tableClick(selectedTable);
        }
    };

    const handleReserve = () => {
        setAnchorEl(null);

        if (selectedTable) {
            const deleteEvent = new CustomEvent("reserveTable", {
                detail: { table: selectedTable },
            });
            document.dispatchEvent(deleteEvent);
        }
    };

    const handleStartMove = () => {
        setAnchorEl(null);

        if (selectedTable) {
            if (handleOnChangeMove) handleOnChangeMove(true);
        }
    };

    const handleMoveOrders = () => {
        setAnchorEl(null);

        if (selectedTable) {
            setSelectable(true);
        }
    };

    const handleMoveOrdersFromTakeAway = (data) => {
        setAnchorEl(null);

        if (selectedTable) {
            setSelectable(true);
        }
    };

    useEffect(() => {
        document.addEventListener(
            "requestMoveToTable",
            handleMoveOrdersFromTakeAway
        );
        return () => {
            document.removeEventListener(
                "requestMoveToTable",
                handleMoveOrdersFromTakeAway
            );
        };
    }, []);

    const onConfirmCancelOrderMove = () => {
        setSelectable(false);
    };

    const [targetTable, setTargetTable] = useState(null);
    const handleTargetTableClicked = (table) => {
        if (table.name === selectedTable.name) {
            //todo warning modal
            alert("Please select a different table.");
        } else {
            setTargetTable(table);
            setMovingOrdersOpen(true);
        }
    };

    const isSelectedOrderPrinted = useMemo(() => {
        if (selectedTable && selectedTable.orders) {
            return Object.values(selectedTable.orders).some(
                (order) => order.invoice_printed == 1
            );
        }
        return false;
    }, [selectedTable]);

    return (
        <>
            <div
                style={{
                    backgroundImage: backgroundUrl
                        ? `url("${backgroundUrl}")`
                        : "none",
                    width: imageWidth,
                    height: imageHeight,
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    marginTop: "5px",
                    marginLeft: "5px",
                }}
            >
                {selectable && (
                    <SwitchTableAlert onConfirm={onConfirmCancelOrderMove} />
                )}
                {backgroundUrl &&
                    tables &&
                    tables.map((table, index) => (
                        <DragableTable
                            key={table.name}
                            table={table}
                            handleOpenMenu={handleOpenMenu}
                            open={open}
                            handleStartMoveClicked={handleStartMove}
                            moving={moving}
                            selectable={selectable}
                            imageWidth={oreginalImageWidth}
                            imageHeight={oreginalImageHeight}
                            calculatedImageWidth={imageWidth}
                            calculatedImageHeight={imageHeight}
                            onTargetTableClicked={handleTargetTableClicked}
                        ></DragableTable>
                    ))}

                {!moving && (
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                        }}
                    >
                        <MenuItem
                            className={classes.MenuItem}
                            onClick={() => {
                                handleOrder();
                                setAnchorEl(null);
                            }}
                        >
                            <ListItemIcon>
                                <Add />
                            </ListItemIcon>
                            Order
                        </MenuItem>
                        {!isRoleService(userInfo.role_profile_name)
                            ? selectedTable &&
                              selectedTable.numberOfOrders === 0 &&
                              (selectedTable.reserved ? (
                                  <MenuItem
                                      className={classes.MenuItem}
                                      onClick={() => {
                                          handleReserve();
                                          setAnchorEl(null);
                                      }}
                                  >
                                      View/Update reservation
                                  </MenuItem>
                              ) : (
                                  <MenuItem
                                      className={classes.MenuItem}
                                      key="2"
                                      onClick={() => {
                                          handleReserve();
                                          setAnchorEl(null);
                                      }}
                                  >
                                      <ListItemIcon>
                                          <PersonAddAlt />
                                      </ListItemIcon>
                                      Reserve table
                                  </MenuItem>
                              ))
                            : null}
                        {selectedTable && selectedTable.numberOfOrders > 0 ? (
                            <MenuItem
                                className={classes.MenuItem}
                                onClick={() => {
                                    handleMoveOrders();
                                    setAnchorEl(null);
                                }}
                            >
                                <ListItemIcon>
                                    <CompareArrows />
                                </ListItemIcon>
                                Move orders
                            </MenuItem>
                        ) : null}
                        {selectedTable && selectedTable.numberOfOrders > 0 ? (
                            <MenuItem
                                className={classes.MenuItem}
                                disabled={
                                    restaurantSettings?.docs[0]
                                        ?.print_order_to_pay == 1
                                        ? !isSelectedOrderPrinted
                                        : false
                                }
                                onClick={() => {
                                    handleCloseTable();
                                    setAnchorEl(null);
                                }}
                            >
                                <ListItemIcon>
                                    <Receipt />
                                </ListItemIcon>
                                close table
                            </MenuItem>
                        ) : null}
                        {discountEnabled &&
                        selectedTable &&
                        selectedTable.numberOfOrders > 0 ? (
                            <MenuItem
                                className={classes.MenuItem}
                                onClick={() => {
                                    togglePrintOrder();
                                    setAnchorEl(null);
                                }}
                            >
                                {/* <MenuItem onClick={handlePrintOrders}> */}
                                <ListItemIcon>
                                    <Print />
                                </ListItemIcon>
                                Print Order
                            </MenuItem>
                        ) : null}
                    </Menu>
                )}
            </div>
            <MoveOrdersDialog
                isOpen={movingOrdersOpen}
                targetTable={targetTable}
                selectedTable={selectedTable}
                onClose={() => {
                    setSelectable(false);
                    setTargetTable(null);
                }}
            />
            <PrintOrderModal
                open={printOrderModal}
                onClose={togglePrintOrder}
                orders={selectedTable?.orders}
            />
        </>
    );
};

export default Room;
